import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { 
  getCandidates, 
  getCandidate,
  createCandidate as createCandidateAPI,
  updateCandidate as updateCandidateAPI,
  deleteCandidate as deleteCandidateAPI,
  bulkImportCandidates as bulkImportAPI
} from '../lib/api';
import { matchesStatusFilter, matchesContactFilter } from '../lib/utils/candidateStatus';

const REALTIME_POLL_MS = 4000;

/**
 * Hook to fetch and manage a single candidate
 */
export const useCandidate = (candidateId, { realtime = false } = {}) => {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const [candidate, setCandidate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const hasLoadedRef = useRef(false);

  const fetchCandidate = useCallback(async (options = {}) => {
    const { silent } = options;
    if (!userId || !candidateId) {
      setLoading(false);
      hasLoadedRef.current = false;
      return;
    }

    const useSilent = silent ?? hasLoadedRef.current;

    try {
      if (!useSilent) setLoading(true);
      setError(null);
      const data = await getCandidate(candidateId);
      
      // Verify ownership
      if (data && data.user_id === userId) {
        setCandidate(data);
        hasLoadedRef.current = true;
      } else {
        setError('Candidate not found or access denied');
        setCandidate(null);
        hasLoadedRef.current = false;
      }
    } catch (err) {
      console.error('Error fetching candidate:', err);
      setError(err.message || 'Failed to fetch candidate');
      setCandidate(null);
      hasLoadedRef.current = false;
    } finally {
      if (!useSilent) setLoading(false);
    }
  }, [userId, candidateId]);

  useEffect(() => {
    hasLoadedRef.current = false;
  }, [candidateId, userId]);

  useEffect(() => {
    fetchCandidate();
  }, [fetchCandidate]);

  useEffect(() => {
    if (!realtime || !candidateId) return undefined;

    const channel = supabase
      .channel(`candidate-live-${candidateId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'candidates',
          filter: `id=eq.${candidateId}`,
        },
        (payload) => {
          if (payload?.new?.id) {
            setCandidate((prev) => ({ ...(prev || {}), ...payload.new }));
          }
          fetchCandidate({ silent: true });
        },
      )
      .subscribe();

    const pollTimer = setInterval(() => {
      fetchCandidate({ silent: true });
    }, REALTIME_POLL_MS);

    return () => {
      clearInterval(pollTimer);
      supabase.removeChannel(channel);
    };
  }, [realtime, candidateId, fetchCandidate]);

  const updateCandidate = useCallback(async (updates, options) => {
    if (!candidateId) return;

    try {
      const updatedCandidate = await updateCandidateAPI(candidateId, updates, options);
      setCandidate(updatedCandidate);
      return updatedCandidate;
    } catch (err) {
      console.error('Error updating candidate:', err);
      throw err;
    }
  }, [candidateId]);

  return { 
    candidate, 
    loading, 
    error, 
    refetch: fetchCandidate,
    updateCandidate
  };
};

/**
 * Hook to fetch and manage multiple candidates
 */
export const useCandidates = (filters = {}) => {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const hasLoadedRef = useRef(false);
  const filtersKey = JSON.stringify(filters);
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  const fetchCandidates = useCallback(async (options = {}) => {
    const { silent } = options;
    if (!userId) {
      setLoading(false);
      hasLoadedRef.current = false;
      return;
    }

    const currentFilters = filtersRef.current;
    const useSilent = silent ?? hasLoadedRef.current;

    try {
      if (!useSilent) setLoading(true);
      const data = await getCandidates(userId, currentFilters);
      
      let filteredData = data;
      if (currentFilters.status && currentFilters.status !== 'prioritized') {
        filteredData = filteredData.filter((candidate) =>
          matchesStatusFilter(candidate, currentFilters.status),
        );
      }
      if (currentFilters.contact) {
        filteredData = filteredData.filter((candidate) =>
          matchesContactFilter(candidate, currentFilters.contact),
        );
      }
      
      setCandidates(filteredData);
      hasLoadedRef.current = true;
      setError(null);
    } catch (err) {
      console.error('Error fetching candidates:', err);
      setError(err.message);
    } finally {
      if (!useSilent) setLoading(false);
    }
  }, [userId, filtersKey]);

  useEffect(() => {
    fetchCandidates();
  }, [fetchCandidates]);

  const createCandidate = async (candidateData) => {
    if (!userId) return { data: null, error: 'No user authenticated' };

    try {
      const newCandidate = await createCandidateAPI(userId, candidateData);
      setCandidates(prev => [newCandidate, ...prev]);
      return { data: newCandidate, error: null };
    } catch (err) {
      console.error('Error creating candidate:', err);
      return { data: null, error: err.message };
    }
  };

  const updateCandidate = async (candidateId, updates, options) => {
    try {
      const updated = await updateCandidateAPI(candidateId, updates, options);
      setCandidates(prev => prev.map(c => c.id === candidateId ? updated : c));
      return { data: updated, error: null };
    } catch (err) {
      console.error('Error updating candidate:', err);
      return { data: null, error: err.message };
    }
  };

  const deleteCandidate = async (candidateId) => {
    try {
      await deleteCandidateAPI(candidateId);
      setCandidates(prev => prev.filter(c => c.id !== candidateId));
      return { error: null };
    } catch (err) {
      console.error('Error deleting candidate:', err);
      return { error: err.message };
    }
  };

  const bulkImport = async (candidatesArray) => {
    if (!userId) return { data: null, error: 'No user authenticated' };

    try {
      const imported = await bulkImportAPI(userId, candidatesArray);
      setCandidates(prev => [...imported, ...prev]);
      return { data: imported, error: null };
    } catch (err) {
      console.error('Error importing candidates:', err);
      return { data: null, error: err.message };
    }
  };

  return {
    candidates,
    loading,
    error,
    createCandidate,
    updateCandidate,
    deleteCandidate,
    bulkImport,
    refetch: fetchCandidates,
  };
};
